import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';

interface ProductCardProps {
  id: string;
  image: string;
  name: string;
  price: number;
  originalPrice?: number;
  badge?: string;
  desc: string;
}

const ProductCard = ({ id, image, name, price, originalPrice, badge, desc }: ProductCardProps) => {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart({ id, name, price, type: 'product', image });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="group flex flex-col bg-white/3 border border-violet-500/15 rounded-2xl overflow-hidden hover:border-violet-500/40 transition-all">
      <div className="relative h-40 overflow-hidden shrink-0">
        <img src={image} alt={name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050010]/70 via-transparent to-transparent" />
        {badge && (
          <span className="absolute top-3 left-3 px-2 py-1 bg-violet-600/50 backdrop-blur-sm text-violet-200 text-xs font-semibold rounded-lg whitespace-nowrap">
            {badge}
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <p className="text-white font-semibold text-sm leading-snug mb-1">{name}</p>
        <p className="text-white/40 text-xs leading-relaxed mb-3 flex-1 line-clamp-2">{desc}</p>
        <div className="flex items-center justify-between gap-2">
          <div>
            <span className="text-white font-bold text-base">&euro;{price.toFixed(2)}</span>
            {originalPrice && (
              <span className="text-white/30 text-xs line-through ml-2">&euro;{originalPrice.toFixed(2)}</span>
            )}
          </div>
          <button
            onClick={handleAddToCart}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer shrink-0 ${
              added
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-violet-600/25 text-violet-300 border border-violet-500/30 hover:bg-violet-600/40'
            }`}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className={`text-xs ${added ? 'ri-check-line' : 'ri-shopping-cart-line'}`} />
            </div>
            {added ? 'Toegevoegd!' : 'In winkelwagen'}
          </button>
        </div>
      </div>
    </div>
  );
};

const BlogSolutions = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [kitAdded, setKitAdded] = useState(false);

  const handleKitAddToCart = () => {
    addToCart({
      id: 'edk-1',
      name: 'Evil Energy Destroy Kit Compleet!',
      price: 79.95,
      type: 'kit',
      image: 'https://public.readdy.ai/ai/img_res/be1ed15e19e2a0703aa8768cb2e9a049.jpg',
    });
    setKitAdded(true);
    setTimeout(() => setKitAdded(false), 2000);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-[#050010] to-[#080015]">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Concrete oplossingen ✦</p>
          <h2 className="text-3xl font-bold text-white mb-4">
            Waarom bescherming nodig is —<br />
            <span className="text-violet-400">en hoe je vandaag start</span>
          </h2>
          <p className="text-white/50 text-sm max-w-2xl mx-auto">
            Bescherming is geen luxe maar een noodzaak — voor baby, ouders, huis en werk.
            Hieronder de drie meest effectieve oplossingen op basis van sjamaanse kennis.
          </p>
        </div>

        {/* ── BLOK 1: Dagelijkse bescherming ───────────────────────── */}
        <div className="mb-12 p-7 bg-violet-600/8 border border-violet-500/20 rounded-3xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-violet-600/25 shrink-0">
              <i className="ri-shield-check-line text-violet-400 text-xl" />
            </div>
            <div>
              <span className="text-violet-400/60 text-xs uppercase tracking-wide font-semibold">Blok 1</span>
              <h3 className="text-white font-bold text-xl">Dagelijkse bescherming</h3>
            </div>
          </div>
          <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-3xl">
            Bescherming werkt het best als het een dagelijkse gewoonte is. Net zoals je dagelijks doucht,
            verdient je energetisch veld dagelijkse reiniging en bescherming. Deze producten zijn ontworpen voor dagelijks gebruik —
            snel, effectief en makkelijk te integreren.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <ProductCard
              id="bp-1"
              image="https://readdy.ai/api/search-image?query=luxury%20blue%20protect%20soap%20bar%20with%20deep%20royal%20blue%20color%20mystical%20energy%20cleansing%20spiritual%20protection%20soap%20on%20dark%20elegant%20background%20with%20subtle%20shimmer%20blue%20glow%20aura%20protection%20formula&width=600&height=600&seq=bp1&orientation=squarish"
              name="Blue Protect Soap"
              price={14.95}
              badge="Exclusief"
              desc="Eeuwenoud sjamaans recept met puur indigofera-extract. Dagelijkse reinigingszeep zonder toevoegingen."
            />
            <ProductCard
              id="bp-4"
              image="https://readdy.ai/api/search-image?query=blue%20protect%20spray%20bottle%20mystical%20dark%20blue%20aura%20cleansing%20spray%20elegant%20glass%20bottle%20with%20indigo%20blue%20liquid%20shimmer%20protection%20energy%20cleansing%20spiritual%20product%20premium%20dark%20background%20display&width=600&height=600&seq=bp4&orientation=squarish"
              name="Blue Protect Spray (120ml)"
              price={14.95}
              badge="Bestseller"
              desc="Directe beschermingsspray met puur indigofera-extract. Beschermt aura en ruimte in seconden."
            />
            <ProductCard
              id="ee-1"
              image="https://readdy.ai/api/search-image?query=dark%20mystical%20handmade%20protection%20soap%20bar%20deep%20amber%20brown%20hing%20asafoetida%20resin%20spiritual%20evil%20eye%20protection%20soap%20on%20obsidian%20black%20background%20warm%20golden%20glow%20ancient%20ritual%20cleansing%20soap%20premium%20dark%20display&width=600&height=600&seq=eenew1&orientation=squarish"
              name="Evil Energy Destroy Zeep"
              price={12.95}
              badge="Dagelijks gebruik"
              desc="Dagelijkse beschermingszeep met hing — weerdt kwade energieën en het boze oog bij elk bad."
            />
            <ProductCard
              id="ee-5"
              image="https://readdy.ai/api/search-image?query=dark%20obsidian%20protection%20spray%20bottle%20deep%20red%20amber%20mystical%20glow%20evil%20energy%20shield%20spiritual%20protection%20hing%20spray%20bottle%20dark%20dramatic%20background%20ancient%20power%20premium%20product%20dark%20stone%20display&width=600&height=600&seq=eenew5&orientation=squarish"
              name="Evil Energy Destroy Spray"
              price={14.95}
              badge="Bestseller"
              desc="Hing in spray — directe ruimtebescherming zonder vuur. Ideaal op kantoor, in de auto of onderweg."
            />
          </div>
        </div>

        {/* ── BLOK 2: Reiniging ─────────────────────────────────── */}
        <div className="mb-12 p-7 bg-fuchsia-600/6 border border-fuchsia-500/20 rounded-3xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-fuchsia-600/25 shrink-0">
              <i className="ri-sparkling-line text-fuchsia-400 text-xl" />
            </div>
            <div>
              <span className="text-fuchsia-400/60 text-xs uppercase tracking-wide font-semibold">Blok 2</span>
              <h3 className="text-white font-bold text-xl">Huis en energie opschonen</h3>
            </div>
          </div>
          <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-3xl">
            Reiniging is vooral belangrijk na bezoek, bij verhuizing en bij kinderen die onrustig zijn.
            Een schone energetische ruimte is de basis voor rust, bescherming en herstel — voor het hele gezin.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <ProductCard
              id="ee-3"
              image="https://readdy.ai/api/search-image?query=ancient%20mystical%20hing%20asafoetida%20incense%20powder%20golden%20brown%20powder%20in%20dark%20ceremonial%20container%20smoke%20rising%20spiritual%20cleansing%20incense%20burning%20ritual%20protection%20dark%20stone%20background%20warm%20amber%20glow%20ancient%20apothecary%20premium&width=600&height=600&seq=eenew3&orientation=squarish"
              name="Evil Energy Destroy Hing Poeder"
              price={9.95}
              badge="Wierook"
              desc="Puur hing poeder om te branden als reinigingswierook. Verjaagt kwade energieën direct uit de ruimte."
            />
            <ProductCard
              id="ee-6"
              image="https://readdy.ai/api/search-image?query=elegant%20dark%20mystical%20cologne%20bottle%20amber%20golden%20hing%20asafoetida%20cologne%20dark%20glass%20bottle%20spiritual%20protection%20ancient%20ritual%20cologne%20dark%20background%20warm%20amber%20glow%20premium%20luxury%20spiritual%20fragrance%20product%20display%20dark%20stone&width=600&height=600&seq=eenew6&orientation=squarish"
              name="Evil Energy Destroy Cologne"
              price={17.95}
              badge="Nieuw"
              desc="Als badtoevoeging of dweelmiddel — verspreidt bescherming door het hele huis van onder tot boven."
            />
            <ProductCard
              id="bp-2"
              image="https://readdy.ai/api/search-image?query=blue%20protective%20crystal%20balls%20spheres%20spiritual%20cleansing%20on%20dark%20background%20deep%20blue%20indigo%20color%20glowing%20energy%20protection%20aura%20cleanse%20mystical%20product%20dark%20elegant%20display&width=600&height=600&seq=bp2&orientation=squarish"
              name="Blue Protect Balls (5 Balls)"
              price={14.95}
              badge="Nieuw"
              desc="Handgemaakte beschermingsballen met puur blauw extract. Los op in bad of zet in een glas water met een scheutje Evil Energy Destroy Cologne en plaats in de hoeken van ruimtes."
            />
          </div>
        </div>

        {/* ── BLOK 3: Begeleiding + Destroy Kit ──────────────────── */}
        <div className="p-7 bg-gradient-to-br from-rose-900/10 to-violet-900/10 border border-rose-500/15 rounded-3xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-rose-600/25 shrink-0">
              <i className="ri-user-star-line text-rose-400 text-xl" />
            </div>
            <div>
              <span className="text-rose-400/60 text-xs uppercase tracking-wide font-semibold">Blok 3</span>
              <h3 className="text-white font-bold text-xl">Begeleiding &amp; extreme gevallen</h3>
            </div>
          </div>
          <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-3xl">
            Soms is de situatie te complex voor standaard bescherming. Bij aanhoudende klachten, aanwezigheid van entiteiten
            of extreme energetische belasting is gerichte begeleiding of de Destroy Kit de aangewezen oplossing.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Destroy Kit */}
            <div className="group flex gap-5 p-5 bg-white/4 border border-rose-500/20 rounded-2xl hover:border-rose-500/50 transition-all">
              <div
                className="w-24 h-24 rounded-xl overflow-hidden shrink-0 cursor-pointer"
                onClick={() => navigate('/evil-energy-destroy-kit')}
              >
                <img
                  src="https://readdy.ai/api/search-image?query=complete%20spiritual%20ritual%20kit%20flat%20lay%20sage%20smudge%20stick%20abalone%20shell%20owl%20feather%20white%20candle%20charcoal%20amber%20herbs%20incense%20dark%20stone%20background%20warm%20dramatic%20candlelight%20moody%20premium%20product%20photography%20dark%20ritual%20kit%20everything%20included%20professional%20display&width=600&height=600&seq=edk1&orientation=squarish"
                  alt="Evil Energy Destroy Kit"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform"
                />
              </div>
              <div className="flex-1">
                <span className="inline-block px-2 py-0.5 bg-rose-500/20 text-rose-300 text-xs rounded-full mb-2 whitespace-nowrap">Extreme gevallen</span>
                <h4 className="text-white font-bold text-sm mb-1">Evil Energy Destroy Kit Compleet!</h4>
                <p className="text-white/40 text-xs leading-relaxed mb-3 line-clamp-2">Compleet doe-het-zelf pakket voor baby-/kinderkamers, nieuwe woning/zaak, of aanwezigheid van entiteiten.</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold">&euro;79,95</span>
                    <span className="text-white/30 text-xs line-through">&euro;99,95</span>
                    <span className="text-rose-300 text-xs font-semibold">-20%</span>
                  </div>
                  <button
                    onClick={handleKitAddToCart}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      kitAdded
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-rose-600/25 text-rose-300 border border-rose-500/30 hover:bg-rose-600/40'
                    }`}
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`text-xs ${kitAdded ? 'ri-check-line' : 'ri-shopping-cart-line'}`} />
                    </div>
                    {kitAdded ? 'Toegevoegd!' : 'In winkelwagen'}
                  </button>
                </div>
              </div>
            </div>

            {/* Consult */}
            <div
              onClick={() => navigate('/consultations')}
              className="group flex gap-5 p-5 bg-white/4 border border-violet-500/20 rounded-2xl hover:border-violet-500/50 transition-all cursor-pointer"
            >
              <div className="w-24 h-24 flex items-center justify-center rounded-xl bg-violet-600/20 shrink-0">
                <i className="ri-user-voice-line text-violet-400 text-4xl" />
              </div>
              <div className="flex-1">
                <span className="inline-block px-2 py-0.5 bg-violet-500/20 text-violet-300 text-xs rounded-full mb-2 whitespace-nowrap">Persoonlijk advies</span>
                <h4 className="text-white font-bold text-sm mb-1">Persoonlijk Spiritueel Consult</h4>
                <p className="text-white/40 text-xs leading-relaxed mb-2">Persoonlijke analyse van jouw situatie. Gericht op gezin, werk of specifieke problematiek. Online of telefonisch.</p>
                <span className="flex items-center gap-1 text-violet-300 text-xs font-semibold">
                  Plan een afspraak <i className="ri-arrow-right-line" />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Mid-page CTA */}
        <div className="mt-10 text-center">
          <button
            onClick={() => navigate('/shop')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold rounded-full whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer"
          >
            <i className="ri-store-2-line" />
            Bekijk alle beschermingsproducten
          </button>
        </div>
      </div>
    </section>
  );
};

export default BlogSolutions;
